import styled, {css} from 'styled-components'
import {Scrollbar} from '@components/ui/scrollbar/Scrollbar.tsx'
import * as RadixScrollArea from '@radix-ui/react-scroll-area'

const zIndexes = {
    pinnedRow: 1,
    header: 2,
    pagination: 2,
    pinnedHeaderCell: 3,
    scrollbar: 4
}

export const StyledTableWrapper = styled.div(
    ({theme: {palette}}) => css`
        width: 100%;
        display: grid;
        grid-template-rows: minmax(0, 1fr) auto;
        overflow: hidden;
        border: 1px solid ${palette.neutral['200']};
        background-color: ${palette.neutral.white};
        isolation: isolate;
    `
)

export const StyledTableContainer = styled.div`
    width: 100%;
    overflow: hidden;
`

export const StyledTableContainerHorizontalScrollbar = styled(Scrollbar)<{$hasPagination: boolean}>(
    ({theme: {spacing}, $hasPagination}) => css`
        z-index: ${zIndexes.scrollbar};
        ${!$hasPagination &&
        css`
            margin-left: ${spacing * 2}px;
        `}
    `
)

export const StyledTableContainerVerticalScrollbar = styled(Scrollbar)(
    () => css`
        z-index: ${zIndexes.scrollbar};
    `
)

export const StyledTableContainerScrollbarCorner = styled(RadixScrollArea.Corner)`
    width: 8px;
    height: 8px;
`

export const StyledTableHeader = styled.div(
    ({theme: {palette, spacing}}) => css`
        position: sticky;
        z-index: ${zIndexes.header};
        top: 0;
        display: flex;
        align-items: center;
        border-bottom: 1px solid ${palette.neutral['200']};
        background-color: ${palette.neutral['50']};
        padding: 0 ${spacing * 6}px;
    `
)

export const StyledTableHeaderCell = styled.div<{$isPinned: boolean}>(
    ({theme: {spacing, palette}, $isPinned}) => css`
        position: relative;
        overflow-x: hidden;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: ${spacing}px;
        height: 44px;
        background-color: ${palette.neutral['50']};
        padding: 0 ${spacing * 3}px;
        &:first-child {
            padding-left: 0;
        }
        &:last-child {
            padding-right: 0;
        }

        ${$isPinned &&
        css`
            position: sticky;
            z-index: ${zIndexes.pinnedHeaderCell};
        `}

        &:not(:hover) {
            & .column-resizer {
                opacity: 0;
            }
        }
    `
)

export const StyledTableHeaderCellLabel = styled.div(
    ({theme: {typography, palette}}) => css`
        ${typography.textSm};
        font-weight: 500;
        color: ${palette.neutral['600']};
        overflow-x: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 100%;
    `
)

export const StyledTableColumnResizer = styled.div(
    ({theme: {palette, spacing, transition}}) => css`
        position: absolute;
        height: 100%;
        top: 0;
        right: 0;
        width: ${spacing}px;
        background-color: ${palette.primary['400']};
        cursor: col-resize;
        user-select: none;
        touch-action: none;
        transition: ${transition};

        &:hover {
            background-color: ${palette.primary['600']};
        }
    `
)

export const StyledTableBodyRow = styled.div<{$isSelected: boolean}>(
    ({theme: {palette, transition, spacing}, $isSelected}) => css`
        width: 100%;
        transition: ${transition};
        background-color: ${palette.neutral.white};
        padding: 0 ${spacing * 6}px;

        ${$isSelected &&
        css`
            background-color: ${palette.neutral['50']};
        `}

        &:hover,
        &:hover .pinned {
            background-color: ${palette.neutral['50']};
        }
        &:not(:last-child) {
            border-bottom: 1px solid ${palette.neutral['200']};
        }
        cursor: pointer;
        &:not(:hover) .show-on-hover {
            transition: none;
            visibility: hidden;
        }
    `
)

export const StyledTableBodyCell = styled.div<{$isPinned?: boolean; $isSelected?: boolean}>(
    ({theme: {spacing, palette, transition}, $isPinned, $isSelected}) => css`
        height: 48px;
        display: flex;
        align-items: center;
        padding: 0 ${spacing * 3}px;
        &:first-child {
            padding-left: 0;
        }
        &:last-child {
            padding-right: 0;
        }

        ${$isPinned &&
        css`
            position: sticky;
            background-color: ${$isSelected ? palette.neutral['25'] : palette.neutral.white};
            transition: ${transition};
            z-index: ${zIndexes.pinnedRow};
        `}
    `
)

export const StyledBodyCellText = styled.p<{
    $variant?: 'light' | 'normal' | 'dark' | 'danger'
    $weight?: 'light' | 'regular' | 'bold'
}>(
    ({theme: {palette, typography}, $variant, $weight}) => css`
        color: ${$variant == 'light'
            ? palette.neutral['400']
            : $variant == 'dark'
              ? palette.neutral['950']
              : $variant == 'danger'
                ? palette.danger['600']
                : palette.neutral['700']};
        font-weight: ${$weight == 'light' ? 300 : $weight == 'bold' ? 500 : 400};
        ${typography.textSm};
        overflow-x: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        & a {
            text-decoration: underline;
            cursor: pointer;
        }
    `
)

export const StyledTablePagination = styled.div(
    ({theme: {palette, spacing}}) => css`
        position: sticky;
        z-index: ${zIndexes.pagination};
        bottom: 0;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        align-items: center;
        border-top: 1px solid ${palette.neutral['200']};
        background-color: ${palette.neutral.white};
        padding: ${spacing * 3}px ${spacing * 6}px;
    `
)

export const StyledTablePaginationLimit = styled.div(
    ({theme: {typography, spacing, palette}}) => css`
        display: flex;
        align-items: center;
        gap: ${spacing * 2}px;
        ${typography.textSm};
        color: ${palette.neutral['600']};
        & b {
            color: ${palette.neutral['950']};
            font-weight: 500;
        }
    `
)
